
import {
  defineComponent, onBeforeMount, reactive,
} from 'vue';
import draggable from 'vuedraggable';
import { Modal, message } from 'ant-design-vue';
import { useAppBase } from '@/core/composables/AppBase';
import ServicoApp from '@/servicos/ServicoApp';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import Icone from '@/core/components/UI/Icone.vue';
import Card from '@/core/components/UI/Card.vue';
import ServicoArquivo from '@/servicos/ServicoArquivo';
import { IArquivo } from '@/models/Entidades/IArquivo';
import { IBanner } from '@/models/Entidades/IBanner';
import { EDestinoBanner } from '@/models/Enumeradores/EDestinoBanner';
import { ETipoBanner } from '@/models/Enumeradores/ETipoBanner';

interface IBannerTela extends IBanner {
  codigoTemporario: string;
}

interface ITipoBannerOpcao {
  valor: ETipoBanner;
  descricao: string;
}

export default defineComponent({
  name: 'Erp',
  components: {
    Card,
    Icone,
    draggable,
  },
  setup() {
    const {
      appBase,
    } = useAppBase();

    const servicoApp = new ServicoApp();
    const servicoArquivo = new ServicoArquivo();

    const tiposBanner: ITipoBannerOpcao[] = [
      { valor: ETipoBanner.PaginaPrincipal, descricao: 'Página Principal' },
      // { valor: ETipoBanner.Ofertas, descricao: 'Ofertas' },
      // { valor: ETipoBanner.Destaques, descricao: 'Destaques' },
      // { valor: ETipoBanner.Busca, descricao: 'Busca' },
      // { valor: ETipoBanner.Categorias, descricao: 'Categorias' },
    ];

    const state = reactive({
      carregando: false,
      enviandoImagem: false,
      salvandoTipo: 0 as ETipoBanner | 0,
      accordionAberto: ETipoBanner.PaginaPrincipal as ETipoBanner,
      bannersPorTipo: {
        [ETipoBanner.PaginaPrincipal]: [] as IBannerTela[],
        [ETipoBanner.Ofertas]: [] as IBannerTela[],
        [ETipoBanner.Destaques]: [] as IBannerTela[],
        [ETipoBanner.Busca]: [] as IBannerTela[],
        [ETipoBanner.Categorias]: [] as IBannerTela[],
      },
    });

    function gerarCodigoTemporario() {
      return `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
    }

    function novoBanner(tipo: ETipoBanner, ordem: number): IBannerTela {
      return {
        codigoTemporario: gerarCodigoTemporario(),
        codigo: 0,
        tipo,
        imagemDesktop: '',
        imagemMobile: '',
        descricao: '',
        link: '',
        destino: EDestinoBanner.MesmaPagina,
        ordem,
      };
    }

    function converterBannerTela(banner: IBanner): IBannerTela {
      return {
        ...banner,
        codigoTemporario: gerarCodigoTemporario(),
        destino: banner.destino || EDestinoBanner.MesmaPagina,
        descricao: banner.descricao || '',
        imagemDesktop: banner.imagemDesktop || '',
        imagemMobile: banner.imagemMobile || '',
        link: banner.link || '',
      };
    }

    function obterBannersPorTipo(tipo: ETipoBanner): IBannerTela[] {
      return state.bannersPorTipo[tipo] || [];
    }

    function garantirBannerInicial(tipo: ETipoBanner) {
      if (!UtilitarioGeral.validaLista(state.bannersPorTipo[tipo])) {
        state.bannersPorTipo[tipo] = [novoBanner(tipo, 1)];
      }
    }

    function organizarBanners(tipo: ETipoBanner) {
      const banners = state.bannersPorTipo[tipo];

      if (UtilitarioGeral.validaLista(banners)) {
        const bannersOrdenados: IBannerTela[] = banners.map((banner, index) => ({
          ...banner,
          tipo,
          ordem: index + 1,
        }));

        state.bannersPorTipo[tipo] = bannersOrdenados;
      }
    }

    function adicionarBanner(tipo: ETipoBanner) {
      const banners = state.bannersPorTipo[tipo];

      banners.push(novoBanner(tipo, banners.length + 1));
      organizarBanners(tipo);
    }

    function removerBanner(tipo: ETipoBanner, banner: IBannerTela) {
      const banners = state.bannersPorTipo[tipo];
      const index = banners.findIndex((c) => c.codigoTemporario === banner.codigoTemporario);

      if (index >= 0) {
        banners.splice(index, 1);
        organizarBanners(tipo);
      }

      garantirBannerInicial(tipo);
    }

    function confirmaExclusao(tipo: ETipoBanner, banner: IBannerTela) {
      Modal.confirm({
        title: 'Você confirma a exclusão deste banner?',
        content: 'Após salvar, este banner será removido desta seção.',
        okText: 'Sim',
        okType: 'danger',
        cancelText: 'Não',
        autoFocusButton: null,
        onOk: () => removerBanner(tipo, banner),
      });
    }

    function montarIdUpload(tipo: ETipoBanner, index: number, versao: 'desktop' | 'mobile') {
      return `selecionarBanner-${tipo}-${index}-${versao}`;
    }

    function selecionarImagemUpload(tipo: ETipoBanner, index: number, versao: 'desktop' | 'mobile') {
      const elemento = document.getElementById(montarIdUpload(tipo, index, versao));

      if (elemento !== null) {
        elemento.click();
      }
    }

    function aplicarImagemBanner(
      tipo: ETipoBanner,
      index: number,
      versao: 'desktop' | 'mobile',
      arquivo: IArquivo,
    ) {
      if (UtilitarioGeral.valorValido(arquivo.link)) {
        const banners = state.bannersPorTipo[tipo];

        if (UtilitarioGeral.validaLista(banners) && banners[index] !== undefined) {
          if (versao === 'desktop') {
            banners[index].imagemDesktop = arquivo.link;
          } else {
            banners[index].imagemMobile = arquivo.link;
          }
        }
      }
    }

    async function enviarImagem(
      event: any,
      tipo: ETipoBanner,
      index: number,
      versao: 'desktop' | 'mobile',
    ) {
      if (!UtilitarioGeral.validaLista(event.target.files)) {
        return;
      }

      state.enviandoImagem = true;

      try {
        const listaArquivos = await servicoArquivo.enviarVarios(event.target.files);

        if (UtilitarioGeral.validaLista(listaArquivos)) {
          aplicarImagemBanner(tipo, index, versao, listaArquivos[0]);
        }

        // event.target.value = '';
      } finally {
        state.enviandoImagem = false;
      }
    }

    async function dropImagem(
      event: any,
      tipo: ETipoBanner,
      index: number,
      versao: 'desktop' | 'mobile',
    ) {
      if (!UtilitarioGeral.validaLista(event.dataTransfer.files)) {
        return;
      }

      state.enviandoImagem = true;

      try {
        const listaArquivos = await servicoArquivo.enviarVarios(event.dataTransfer.files);

        if (UtilitarioGeral.validaLista(listaArquivos)) {
          aplicarImagemBanner(tipo, index, versao, listaArquivos[0]);
        }
      } finally {
        state.enviandoImagem = false;
      }
    }

    function validarBanners(tipo: ETipoBanner): boolean {
      const banners = state.bannersPorTipo[tipo];

      if (!UtilitarioGeral.validaLista(banners)) {
        message.warning('Adicione pelo menos um banner antes de salvar.');
        return false;
      }

      for (let index = 0; index < banners.length; index += 1) {
        const banner = banners[index];

        if (!UtilitarioGeral.valorValido(banner.imagemDesktop)) {
          message.warning(`Informe a imagem desktop do banner ${index + 1}.`);
          return false;
        }

        if (!UtilitarioGeral.valorValido(banner.imagemMobile)) {
          message.warning(`Informe a imagem mobile do banner ${index + 1}.`);
          return false;
        }

        if (!UtilitarioGeral.valorValido(banner.link)) {
          message.warning(`Informe o link do banner ${index + 1}.`);
          return false;
        }
      }

      return true;
    }

    function montarBannersParaSalvar(tipo: ETipoBanner): IBanner[] {
      organizarBanners(tipo);

      return state.bannersPorTipo[tipo].map((banner) => ({
        codigo: banner.codigo,
        tipo: banner.tipo,
        imagemDesktop: banner.imagemDesktop,
        imagemMobile: banner.imagemMobile,
        descricao: banner.descricao,
        link: banner.link,
        destino: banner.destino,
        ordem: banner.ordem,
      }));
    }

    async function carregarBanners() {
      state.carregando = true;

      try {
        tiposBanner.forEach((tipoBanner) => {
          garantirBannerInicial(tipoBanner.valor);
        });

        const banners = await servicoApp.obterBanners();
        const listaBanners = UtilitarioGeral.validaLista(banners) ? banners : [];

        tiposBanner.forEach((tipoBanner) => {
          const bannersTipo = listaBanners
            .filter((banner) => banner.tipo === tipoBanner.valor)
            .sort((a, b) => a.ordem - b.ordem)
            .map((banner) => converterBannerTela(banner));

          state.bannersPorTipo[tipoBanner.valor] = bannersTipo;

          garantirBannerInicial(tipoBanner.valor);
          organizarBanners(tipoBanner.valor);
        });
      } catch (error) {
        message.error('Não foi possível carregar os banners.');

        tiposBanner.forEach((tipoBanner) => {
          garantirBannerInicial(tipoBanner.valor);
          organizarBanners(tipoBanner.valor);
        });
      } finally {
        state.carregando = false;
      }
    }

    async function salvarBanners(tipo: ETipoBanner) {
      if (!validarBanners(tipo)) {
        return;
      }

      state.salvandoTipo = tipo;

      try {
        const banners = montarBannersParaSalvar(tipo);

        await servicoApp.atualizarBanners(tipo, banners);

        message.success('Banners atualizados com sucesso.');

        await carregarBanners();
      } finally {
        state.salvandoTipo = 0;
      }
    }

    onBeforeMount(async () => {
      await carregarBanners();
    });

    return {
      state,
      appBase,
      tiposBanner,
      ETipoBanner,
      EDestinoBanner,
      obterBannersPorTipo,
      adicionarBanner,
      removerBanner,
      confirmaExclusao,
      selecionarImagemUpload,
      montarIdUpload,
      enviarImagem,
      dropImagem,
      salvarBanners,
      organizarBanners,
    };
  },
});
